export const articles = [
  {
    id: 1,
    title: '深入理解 React 19 的并发渲染机制',
    slug: 'react-19-concurrent-rendering',
    category: 'programming',
    excerpt: 'React 19 带来了全新的并发渲染架构，本文将深入分析其内部实现原理，探讨 Suspense、Transition 等特性如何提升用户体验。',
    content: `<h2>React 19 并发渲染概述</h2>
<p>React 19 在并发渲染方面进行了重大升级，为开发者提供了更强大的工具来构建流畅的用户界面。并发渲染允许 React 同时准备多个版本的 UI，从而在保持响应性的同时处理复杂的渲染任务。</p>
<h3>核心概念</h3>
<p>并发渲染的核心在于可中断渲染。React 可以将渲染工作拆分为多个小单元，并在必要时暂停和恢复这些单元。这意味着高优先级的更新（如用户输入）可以打断低优先级的渲染（如数据加载后的界面更新）。</p>
<h3>useTransition 的实际应用</h3>
<p>useTransition 是并发模式中最常用的 Hook 之一。它允许你将某些状态更新标记为"过渡"，从而在更新期间保持界面的响应性。例如，在搜索场景中，你可以将搜索结果更新标记为过渡，这样用户在输入时不会感到卡顿。</p>
<pre><code>const [isPending, startTransition] = useTransition();
startTransition(() => {
  setSearchResults(filteredResults);
});</code></pre>
<h3>Suspense 与数据获取</h3>
<p>React 19 进一步优化了 Suspense 与数据获取的结合使用。通过新的 use() Hook，组件可以直接在渲染期间读取 Promise，而 Suspense 会优雅地处理加载状态。</p>
<h3>性能优化建议</h3>
<p>在实际项目中，建议遵循以下最佳实践：优先使用 startTransition 包裹非紧急更新；合理设置 Suspense 边界；避免在并发模式下使用过时的生命周期方法。</p>`,
    author: '张三',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20professional%20headshot%20portrait%20with%20simple%20light%20background%2C%20clean%20minimalist%20style%2C%20business%20casual%20attire%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-zhang&orientation=squarish',
    date: '2026-08-01',
    readTime: 12,
    tags: ['React', '前端开发', '并发渲染', '性能优化'],
    views: 3842,
    likes: 256,
    comments: 47,
  },
  {
    id: 2,
    title: '零日漏洞挖掘：从信息收集到漏洞利用的完整流程',
    slug: 'zero-day-vulnerability-hunting',
    category: 'security',
    excerpt: '本文将详细介绍零日漏洞挖掘的完整方法论，包括信息收集、模糊测试、代码审计到最终的漏洞利用编写，帮助安全研究人员提升漏洞发现能力。',
    content: `<h2>零日漏洞挖掘方法论</h2>
<p>零日漏洞作为网络安全领域最具价值的研究方向之一，其挖掘过程需要系统化的方法论和扎实的技术功底。本文将从实战角度出发，完整呈现漏洞挖掘的全流程。</p>
<h3>信息收集阶段</h3>
<p>信息收集是漏洞挖掘的第一步，也是最关键的一步。需要获取目标软件的版本信息、历史漏洞记录、使用的第三方库及其版本。常用的信息收集工具包括 Nmap、Shodan 以及针对性的爬虫脚本。</p>
<h3>模糊测试技术</h3>
<p>模糊测试（Fuzzing）是发现未知漏洞的有效手段。AFL++ 和 LibFuzzer 是目前最流行的开源模糊测试框架。通过编写有效的 harness 并配置合适的变异策略，可以大幅提升漏洞发现效率。</p>
<h3>代码审计要点</h3>
<p>针对开源软件，静态代码审计可以发现逻辑漏洞和隐蔽的安全问题。重点关注：内存管理函数的不当使用、输入验证缺失、权限检查遗漏、以及竞态条件问题。</p>
<pre><code>// 典型的内存安全漏洞示例
void vulnerable_function(char *user_input) {
    char buffer[64];
    strcpy(buffer, user_input); // 缓冲区溢出风险
}</code></pre>
<h3>漏洞利用编写</h3>
<p>发现漏洞后，需要验证其可利用性并编写 PoC（概念验证代码）。现代操作系统的安全缓解机制（ASLR、DEP、CFG 等）使得漏洞利用变得更加复杂，需要结合信息泄露等多种技术手段。</p>`,
    author: '李四',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20professional%20headshot%20portrait%20with%20simple%20light%20background%2C%20clean%20minimalist%20style%2C%20dark%20casual%20attire%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-li&orientation=squarish',
    date: '2026-08-02',
    readTime: 18,
    tags: ['网络安全', '漏洞挖掘', '渗透测试', '模糊测试'],
    views: 5621,
    likes: 389,
    comments: 83,
  },
  {
    id: 3,
    title: '大语言模型微调实战：从零训练专属 AI 助手',
    slug: 'llm-fine-tuning-practice',
    category: 'ai',
    excerpt: '基于 LLaMA、Qwen 等开源大语言模型，详细介绍完整的微调流程，包括数据准备、LoRA 微调、量化部署等关键步骤。',
    content: `<h2>大语言模型微调概述</h2>
<p>随着开源大语言模型的蓬勃发展，越来越多的企业和开发者希望基于自身业务数据训练专属的 AI 助手。本文将完整呈现从数据准备到模型部署的全流程。</p>
<h3>数据准备工作</h3>
<p>高质量的训练数据是微调成功的关键。数据需要经过清洗、去重、格式化和质量评估等多个步骤。建议使用多样化的指令数据，覆盖目标应用场景的各个方面。</p>
<h3>LoRA 高效微调</h3>
<p>LoRA（Low-Rank Adaptation）是目前最流行的参数高效微调方法。通过在预训练模型的权重矩阵中添加低秩分解矩阵，LoRA 可以在只训练极少参数的情况下实现接近全量微调的效果。</p>
<pre><code>from peft import LoraConfig, get_peft_model
lora_config = LoraConfig(
    r=16,
    lora_alpha=32,
    target_modules=["q_proj", "v_proj"],
    lora_dropout=0.1,
)
model = get_peft_model(base_model, lora_config)</code></pre>
<h3>量化部署方案</h3>
<p>模型训练完成后，需要通过量化技术减小模型大小并提升推理速度。GPTQ 和 AWQ 是两种常用的权重量化方法，可以在保持模型性能的同时将模型大小减少到原来的四分之一。</p>
<h3>效果评估</h3>
<p>微调后的模型需要在多个维度上进行评估，包括生成质量、响应速度、幻觉率等。建议构建专门的测试集，并采用自动化评估与人工评估相结合的方式。</p>`,
    author: '王五',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20professional%20headshot%20portrait%20with%20simple%20light%20background%2C%20clean%20minimalist%20style%2C%20smart%20casual%20attire%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-wang&orientation=squarish',
    date: '2026-08-03',
    readTime: 15,
    tags: ['人工智能', '大语言模型', 'LoRA', '模型微调'],
    views: 7215,
    likes: 512,
    comments: 96,
  },
  {
    id: 4,
    title: 'Kubernetes 集群自动化运维：从监控到自愈的完整方案',
    slug: 'kubernetes-auto-ops',
    category: 'devops',
    excerpt: '本文分享一套基于 Prometheus + AlertManager + 自定义 Operator 的 K8s 集群自动化运维方案，实现故障自动发现与自愈。',
    content: `<h2>Kubernetes 自动化运维体系</h2>
<p>随着容器化部署的普及，Kubernetes 集群的规模日益增长，传统的人工运维方式已无法满足需求。本文将介绍一套完整的自动化运维方案。</p>
<h3>监控体系搭建</h3>
<p>基于 Prometheus + Grafana 构建全面的监控体系，覆盖集群资源、应用性能、业务指标三个层面。通过 Prometheus Operator 实现监控规则的自动化管理。</p>
<h3>告警策略设计</h3>
<p>合理的告警策略是自动化运维的基础。需要根据业务重要性和故障影响范围，分级设计告警规则。避免告警风暴和告警疲劳是关键。</p>
<h3>自定义 Operator 开发</h3>
<p>使用 Kubebuilder 框架开发自定义 Operator，实现特定场景下的自动运维逻辑。例如，当检测到 Pod 频繁重启时，自动收集诊断信息并尝试重启相关服务。</p>
<pre><code>func (r *AutoOpsReconciler) Reconcile(ctx context.Context, req ctrl.Request) (ctrl.Result, error) {
    pod := &corev1.Pod{}
    if err := r.Get(ctx, req.NamespacedName, pod); err != nil {
        return ctrl.Result{}, client.IgnoreNotFound(err)
    }
    // 自愈逻辑
    return ctrl.Result{}, nil
}</code></pre>
<h3>灰度发布与回滚</h3>
<p>结合 Argo Rollouts 实现渐进式发布，自动监控发布过程中的指标变化，异常时自动触发回滚操作。</p>`,
    author: '赵六',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20professional%20headshot%20portrait%20with%20simple%20light%20background%2C%20clean%20minimalist%20style%2C%20business%20casual%20attire%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-zhao&orientation=squarish',
    date: '2026-08-04',
    readTime: 14,
    tags: ['Kubernetes', 'DevOps', '自动化运维', 'Prometheus'],
    views: 4532,
    likes: 301,
    comments: 58,
  },
  {
    id: 5,
    title: 'PostgreSQL 千万级数据表的查询优化实战',
    slug: 'postgresql-query-optimization',
    category: 'database',
    excerpt: '针对 PostgreSQL 千万级数据表，分享一套从索引设计、查询重写到分区表优化的完整调优方案。',
    content: `<h2>PostgreSQL 查询优化策略</h2>
<p>当数据表规模达到千万级别时，查询性能往往成为系统瓶颈。本文将分享在 PostgreSQL 中的实战优化经验。</p>
<h3>索引策略设计</h3>
<p>合理的索引是查询优化的基础。除了常规的 B-tree 索引外，针对特定场景可以考虑使用 GIN、GiST 或 BRIN 索引。多列索引的列顺序需要根据查询模式精心设计。</p>
<h3>查询计划分析</h3>
<p>EXPLAIN ANALYZE 是分析查询性能的利器。通过阅读查询计划，可以发现全表扫描、排序溢出、连接算法选择不当等性能问题。</p>
<pre><code>EXPLAIN (ANALYZE, BUFFERS) SELECT u.name, COUNT(o.id)
FROM users u JOIN orders o ON u.id = o.user_id
WHERE o.created_at > '2026-01-01'
GROUP BY u.name;</code></pre>
<h3>表分区技术</h3>
<p>对于超大规模表，表分区是一种有效的优化手段。PostgreSQL 支持 Range、List 和 Hash 三种分区方式。按时间范围分区是最常见的应用场景。</p>
<h3>连接池与缓存</h3>
<p>除了数据库层面的优化，应用层面的连接池配置（如 PgBouncer）和查询结果缓存（如 Redis）也能显著提升整体性能。</p>`,
    author: '孙七',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20professional%20headshot%20portrait%20with%20simple%20light%20background%2C%20clean%20minimalist%20style%2C%20casual%20attire%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-sun&orientation=squarish',
    date: '2026-08-05',
    readTime: 11,
    tags: ['PostgreSQL', '数据库优化', '索引设计', '查询性能'],
    views: 3215,
    likes: 218,
    comments: 42,
  },
  {
    id: 6,
    title: 'Flutter 3.x 跨平台开发最佳实践',
    slug: 'flutter-cross-platform-best-practices',
    category: 'mobile',
    excerpt: '总结 Flutter 3.x 在实际项目中的开发经验，涵盖状态管理、性能优化、原生交互等关键话题。',
    content: `<h2>Flutter 跨平台开发指南</h2>
<p>Flutter 3.x 在性能和开发体验上都有了显著提升。本文将分享在实际项目开发中积累的最佳实践经验。</p>
<h3>状态管理方案选型</h3>
<p>Riverpod 是当前 Flutter 社区推荐的状态管理方案。相比 Provider，Riverpod 提供了更好的类型安全性和可测试性。对于复杂应用，建议结合 BLoC 模式使用。</p>
<h3>渲染性能优化</h3>
<p>Flutter 的渲染性能通常不是瓶颈，但不当的 Widget 使用方式可能导致不必要的重绘。使用 const 构造函数、避免在 build 方法中创建复杂对象、合理使用 RepaintBoundary 都是有效的优化手段。</p>
<h3>原生平台交互</h3>
<p>通过 Platform Channel 可以实现 Flutter 与原生代码的高效通信。对于性能敏感的插件，可以考虑使用 FFI（Foreign Function Interface）直接调用原生库。</p>
<pre><code>// Method Channel 示例
static const platform = MethodChannel('com.example.app/channel');
final result = await platform.invokeMethod('getBatteryLevel');</code></pre>
<h3>持续集成与发布</h3>
<p>使用 Codemagic 或 GitHub Actions 搭建自动化的 CI/CD 流水线，实现代码提交后的自动构建、测试和发布。</p>`,
    author: '周八',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20professional%20headshot%20portrait%20with%20simple%20light%20background%2C%20clean%20minimalist%20style%2C%20smart%20casual%20attire%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-zhou&orientation=squarish',
    date: '2026-07-30',
    readTime: 13,
    tags: ['Flutter', '移动开发', '跨平台', 'Riverpod'],
    views: 2890,
    likes: 195,
    comments: 36,
  },
  {
    id: 7,
    title: 'TypeScript 5.x 高级类型体操：从入门到精通',
    slug: 'typescript-advanced-types',
    category: 'programming',
    excerpt: '深入 TypeScript 类型系统，通过实际案例讲解条件类型、映射类型、模板字面量类型等高级特性的应用技巧。',
    content: `<h2>TypeScript 高级类型系统</h2>
<p>TypeScript 的类型系统图灵完备，这意味着你可以在类型层面实现复杂的逻辑运算。掌握高级类型技巧可以大幅提升代码的类型安全性。</p>
<h3>条件类型深入</h3>
<p>条件类型类似于类型层面的 if-else 语句。通过 extends 关键字和 infer 关键字，可以实现类型推断和转换。infer 关键字尤其强大，可以在条件类型中提取类型的组成部分。</p>
<h3>映射类型实战</h3>
<p>映射类型允许你基于已有类型创建新类型。结合 keyof 和索引访问类型，可以实现类型的批量转换。常见的内置映射类型包括 Partial、Required、Readonly 和 Pick。</p>
<pre><code>type DeepReadonly&lt;T&gt; = {
  readonly [K in keyof T]: T[K] extends object
    ? DeepReadonly&lt;T[K]&gt;
    : T[K];
};</code></pre>
<h3>模板字面量类型</h3>
<p>TypeScript 4.1 引入的模板字面量类型允许在类型层面操作字符串。结合递归类型，可以实现字符串的解析、转换和验证。</p>
<h3>实际应用场景</h3>
<p>在构建类型安全的 API 客户端、表单验证库和状态管理库时，高级类型技巧可以显著改善开发体验。</p>`,
    author: '张三',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20professional%20headshot%20portrait%20with%20simple%20light%20background%2C%20clean%20minimalist%20style%2C%20business%20casual%20attire%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-zhang&orientation=squarish',
    date: '2026-07-28',
    readTime: 16,
    tags: ['TypeScript', '前端开发', '类型系统', '编程技巧'],
    views: 4132,
    likes: 342,
    comments: 65,
  },
  {
    id: 8,
    title: 'Web 应用安全防护体系构建指南',
    slug: 'web-security-defense-guide',
    category: 'security',
    excerpt: '从 XSS、CSRF、SQL 注入到供应链安全，全面解析 Web 应用常见安全威胁及防御策略。',
    content: `<h2>Web 应用安全防护体系</h2>
<p>随着 Web 应用的复杂性不断增加，安全防护已成为每个开发团队必须重视的议题。本文将系统性地介绍 Web 安全防护的最佳实践。</p>
<h3>XSS 防御策略</h3>
<p>跨站脚本攻击（XSS）是最常见的 Web 安全威胁之一。防御措施包括：输出编码、内容安全策略（CSP）、HttpOnly Cookie 以及使用现代框架的内置 XSS 防护。</p>
<h3>CSRF 防护方案</h3>
<p>跨站请求伪造（CSRF）攻击可以通过 CSRF Token、SameSite Cookie 属性和 Origin 头验证来防御。现代框架通常提供了内置的 CSRF 防护机制。</p>
<h3>SQL 注入防范</h3>
<p>参数化查询是防止 SQL 注入的最有效手段。永远不要将用户输入直接拼接到 SQL 语句中。使用 ORM 框架时，也应确保使用参数化的查询方式。</p>
<pre><code>// 正确的参数化查询
const query = 'SELECT * FROM users WHERE email = ?';
db.query(query, [userEmail]);</code></pre>
<h3>供应链安全</h3>
<p>第三方依赖的安全风险日益突出。建议使用 npm audit、Snyk 等工具定期扫描依赖漏洞，并建立依赖更新和审查机制。</p>`,
    author: '李四',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Chinese%20male%20professional%20headshot%20portrait%20with%20simple%20light%20background%2C%20clean%20minimalist%20style%2C%20dark%20casual%20attire%2C%20warm%20natural%20lighting&width=80&height=80&seq=avatar-li&orientation=squarish',
    date: '2026-07-25',
    readTime: 10,
    tags: ['Web安全', 'XSS', 'CSRF', '安全防护'],
    views: 3876,
    likes: 278,
    comments: 51,
  },
];

export const getArticleById = (id: number) => {
  return articles.find((article) => article.id === id) || null;
};

export const getArticlesByCategory = (category: string) => {
  return articles.filter((article) => article.category === category);
};

export const getFeaturedArticles = () => {
  return [...articles].sort((a, b) => b.views - a.views).slice(0, 4);
};

export const getLatestArticles = (limit = 6) => {
  return [...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, limit);
};

export const getRelatedArticles = (currentId: number, category: string) => {
  return articles
    .filter((article) => article.category === category && article.id !== currentId)
    .slice(0, 3);
};